import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, Check, X } from 'lucide-react';
import { useUserPrefs } from '../Components/context/UserPrefs';
import { api } from '../services/api';

// Cuentas autorizadas para aprobar activaciones de 2FA de otros usuarios.
// No se usa el rol "Administrador" porque ese rol se otorga a muchas cuentas
// solo para darles acceso a módulos (almacén, contabilidad, HSE, etc.), sin
// que deban tener la capacidad de aprobar el segundo factor de otras cuentas.
const TOTP_APPROVER_EMAILS = ['admin@senerfi.com', 'soporteti@senerfi.com', 'joluguma22@gmail.com'];

export default function TotpApprovals() {
  const navigate = useNavigate();
  const { currentUser } = useUserPrefs();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchNotice, setSearchNotice] = useState('');

  const isAdmin = TOTP_APPROVER_EMAILS.includes(String(currentUser?.email || '').trim().toLowerCase());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.auth.totpAdmin.list();
      if (!res?.ok) throw new Error(res?.error || 'No fue posible cargar las solicitudes.');
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e?.message || 'No fue posible cargar las solicitudes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/'); return; }
    if (!isAdmin) return;
    load();
  }, [currentUser, isAdmin, navigate, load]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      const res = await api.auth.totpAdmin.approve(id);
      if (!res?.ok) throw new Error(res?.error || 'No fue posible aprobar.');
      await load();
    } catch (e) {
      setError(e?.message || 'No fue posible aprobar.');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    const ok = window.confirm('¿Rechazar esta activación? El usuario tendrá que empezar de nuevo con un código diferente.');
    if (!ok) return;
    setBusyId(id);
    try {
      const res = await api.auth.totpAdmin.reject(id);
      if (!res?.ok) throw new Error(res?.error || 'No fue posible rechazar.');
      await load();
    } catch (e) {
      setError(e?.message || 'No fue posible rechazar.');
    } finally {
      setBusyId(null);
    }
  };

  const runSearch = async (e) => {
    if (e) e.preventDefault();
    const q = searchQuery.trim();
    setSearchError('');
    setSearchNotice('');
    if (!q) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await api.auth.totpAdmin.search(q);
      if (!res?.ok) throw new Error(res?.error || 'No fue posible buscar.');
      setSearchResults(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setSearchError(e?.message || 'No fue posible buscar.');
    } finally {
      setSearching(false);
    }
  };

  const resetUser = async (u) => {
    const ok = window.confirm(`¿Resetear la verificación en dos pasos de ${u.name || u.email}?\n\nQuedará sin 2FA activo: la próxima vez que inicie sesión, se le pedirá activar de nuevo (escanear un QR nuevo), y quedará pendiente de aprobación otra vez.`);
    if (!ok) return;
    setBusyId('search-' + u.id);
    setSearchError('');
    setSearchNotice('');
    try {
      const res = await api.auth.totpAdmin.reset(u.id);
      if (!res?.ok) throw new Error(res?.error || 'No fue posible resetear.');
      setSearchNotice(`Verificación reseteada para ${u.name || u.email}. Ya puede volver a activarla desde cero.`);
      await runSearch();
      await load();
    } catch (e) {
      setSearchError(e?.message || 'No fue posible resetear.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={16} /> Volver
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Activaciones de 2FA pendientes</h1>
            <p className="text-sm text-gray-500">Usuarios que confirmaron su app pero aún no están aprobados</p>
          </div>
        </div>

        {!isAdmin ? (
          <div className="px-3 py-2 rounded-lg border text-sm bg-yellow-50 border-yellow-200 text-yellow-700">
            Tu cuenta no está autorizada para aprobar activaciones de 2FA.
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-4 px-3 py-2 rounded-lg border text-sm bg-red-50 border-red-200 text-red-700">{error}</div>
            )}
            {loading ? (
              <p className="text-sm text-gray-500">Cargando...</p>
            ) : rows.length === 0 ? (
              <p className="text-sm text-gray-500">No hay activaciones pendientes por ahora.</p>
            ) : (
              <div className="space-y-2">
                {rows.map((r) => (
                  <div key={r.id} className="flex items-center justify-between border rounded-xl px-4 py-3">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{r.name || 'Sin nombre'}</div>
                      <div className="text-xs text-gray-500">{r.email} {r.area_name ? `· ${r.area_name}` : ''}</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={busyId === r.id}
                        onClick={() => approve(r.id)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
                        style={{ backgroundColor: '#16A34A' }}
                      >
                        <Check size={14} /> Aprobar
                      </button>
                      <button
                        type="button"
                        disabled={busyId === r.id}
                        onClick={() => reject(r.id)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-sm font-semibold disabled:opacity-60"
                      >
                        <X size={14} /> Rechazar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-gray-200">
              <h2 className="text-base font-semibold text-gray-900 mb-1">Resetear verificación de un usuario</h2>
              <p className="text-sm text-gray-500 mb-3">
                Úsalo cuando alguien perdió el celular o la app autenticadora y necesita activar el 2FA de nuevo desde cero
                (le pedirá escanear un QR nuevo en su próximo inicio de sesión).
              </p>
              <form onSubmit={runSearch} className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por nombre o correo..."
                  className="flex-1 border rounded-lg px-3 py-2 text-sm"
                />
                <button
                  type="submit"
                  disabled={searching}
                  className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
                  style={{ backgroundColor: '#2563EB' }}
                >
                  {searching ? 'Buscando...' : 'Buscar'}
                </button>
              </form>

              {searchNotice && (
                <div className="mb-3 px-3 py-2 rounded-lg border text-sm bg-green-50 border-green-200 text-green-700">{searchNotice}</div>
              )}
              {searchError && (
                <div className="mb-3 px-3 py-2 rounded-lg border text-sm bg-red-50 border-red-200 text-red-700">{searchError}</div>
              )}

              {searchResults.length > 0 && (
                <div className="space-y-2">
                  {searchResults.map((u) => {
                    const status = u.totp_enabled ? 'Activo' : (u.totp_awaiting_approval ? 'Pendiente de aprobación' : 'Inactivo');
                    const statusColor = u.totp_enabled ? 'bg-green-100 text-green-700' : (u.totp_awaiting_approval ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600');
                    const canReset = !!u.totp_enabled || !!u.totp_awaiting_approval;
                    return (
                      <div key={u.id} className="flex items-center justify-between border rounded-xl px-4 py-3">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{u.name || 'Sin nombre'}</div>
                          <div className="text-xs text-gray-500">{u.email} {u.area_name ? `· ${u.area_name}` : ''}</div>
                          <span className={`inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full ${statusColor}`}>2FA: {status}</span>
                        </div>
                        <button
                          type="button"
                          disabled={!canReset || busyId === 'search-' + u.id}
                          onClick={() => resetUser(u)}
                          className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-sm font-semibold disabled:opacity-40"
                          title={canReset ? '' : 'Este usuario no tiene 2FA activo ni pendiente'}
                        >
                          Resetear 2FA
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
